import React, { useState, useEffect } from 'react';
import { loteService } from '../services/api';
import { authService } from '../services/auth';
import { toast } from 'react-toastify';
import './ListaLotes.css';

/**
 * Componente ListaLotes
 * Muestra una tabla con todos los lotes y filtros
 */
const ListaLotes = ({ onEditLote, onDeleteLote, onRequestDelete, refreshTrigger }) => {
  const [lotes, setLotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    estado: '',
    areaMin: '',
    areaMax: '',
    precioMin: '',
    precioMax: '',
  });
  // Estado separado para los inputs del filtro (para no disparar búsqueda en cada tecla)
  const [filterInputs, setFilterInputs] = useState({
    estado: '',
    areaMin: '',
    areaMax: '',
    precioMin: '',
    precioMax: '',
  });

  // 👉 estados para edición inline
  const [editingEstadoId, setEditingEstadoId] = useState(null);
  const [estadoTemp, setEstadoTemp] = useState('');
  
  // 👉 estados para modal de cliente
  const [mostrarModalCliente, setMostrarModalCliente] = useState(false);
  const [loteEnEdicion, setLoteEnEdicion] = useState(null);
  const [nuevoEstadoTemp, setNuevoEstadoTemp] = useState('');
  const [datosCliente, setDatosCliente] = useState({
    nombre: '',
    telefono: '',
    email: '',
    notas: ''
  });
  const [erroresForm, setErroresForm] = useState({});

  useEffect(() => {
    const cargar = async () => {
      try {
        setLoading(true);
        const data = await loteService.obtenerLotes(filters);
        setLotes(data);
      } catch (error) {
        console.error('Error al cargar lotes:', error);
        toast.error('Error al cargar lotes: ' + (error.message || 'Error desconocido'));
      } finally {
        setLoading(false);
      }
    };

    cargar();
  }, [filters, refreshTrigger]);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilterInputs((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const aplicarFiltros = () => {
    setFilters(filterInputs);
  };

  const limpiarFiltros = () => {
    setFilters({
      estado: '',
      areaMin: '',
      areaMax: '',
      precioMin: '',
      precioMax: '',
    });
    setFilterInputs({
      estado: '',
      areaMin: '',
      areaMax: '',
      precioMin: '',
      precioMax: '',
    });
  };

  const getEstadoBadge = (estado) => {
    const clases = {
      disponible: 'badge-disponible',
      reservado: 'badge-reservado',
      vendido: 'badge-vendido',
    };
    return <span className={`badge ${clases[estado] || ''}`}>{estado}</span>;
  };

  // 👉 actualizar SOLO el estado
  const actualizarEstado = async (loteId, nuevoEstado) => {
    // Validar que si el estado es "reservado" o "vendido", debe haber cliente
    const lote = lotes.find(l => l._id === loteId);
    
    // Si cambia de reservado a disponible, borrar cliente automáticamente
    if (lote.estado === 'reservado' && nuevoEstado === 'disponible') {
      try {
        await loteService.actualizarLote(loteId, { 
          estado: nuevoEstado,
          cliente: null
        });

        setLotes((prev) =>
          prev.map((l) =>
            l._id === loteId ? { ...l, estado: nuevoEstado, cliente: null } : l
          )
        );

        toast.success('Lote disponible - Cliente eliminado');
      } catch (error) {
        console.error(error);
        toast.error('Error al actualizar estado: ' + (error.message || 'Error desconocido'));
      } finally {
        setEditingEstadoId(null);
      }
      return;
    }
    
    if ((nuevoEstado === 'reservado' || nuevoEstado === 'vendido') && !lote.cliente?.nombre) {
      // Abrir modal para agregar cliente
      setLoteEnEdicion(lote);
      setNuevoEstadoTemp(nuevoEstado);
      setDatosCliente({
        nombre: lote.cliente?.nombre || '',
        telefono: lote.cliente?.telefono || '',
        email: lote.cliente?.email || '',
        notas: lote.cliente?.notas || ''
      });
      setErroresForm({});
      setMostrarModalCliente(true);
      return;
    }

    // Si ya hay cliente o el estado es disponible, actualizar directamente
    try {
      await loteService.actualizarLote(loteId, { estado: nuevoEstado });

      setLotes((prev) =>
        prev.map((l) =>
          l._id === loteId ? { ...l, estado: nuevoEstado } : l
        )
      );

      toast.success('Estado actualizado correctamente');
    } catch (error) {
      console.error(error);
      toast.error('Error al actualizar estado: ' + (error.message || 'Error desconocido'));
    } finally {
      setEditingEstadoId(null);
    }
  };

  // 👉 manejar cambios en el formulario del cliente
  const handleDatosClienteChange = (e) => {
    const { name, value } = e.target;
    setDatosCliente(prev => ({
      ...prev,
      [name]: value
    }));
    // Limpiar error del campo cuando el usuario empieza a escribir
    if (erroresForm[name]) {
      setErroresForm(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  // 👉 validar y guardar cliente
  const handleGuardarCliente = async () => {
    // Validar campos obligatorios
    const nuevosErrores = {};
    if (!datosCliente.nombre.trim()) {
      nuevosErrores.nombre = 'El nombre es obligatorio';
    }
    if (!datosCliente.telefono.trim()) {
      nuevosErrores.telefono = 'El teléfono es obligatorio';
    }

    if (Object.keys(nuevosErrores).length > 0) {
      setErroresForm(nuevosErrores);
      return;
    }

    try {
      const datosActualizacion = {
        estado: nuevoEstadoTemp,
        cliente: {
          nombre: datosCliente.nombre.trim(),
          telefono: datosCliente.telefono.trim(),
          email: datosCliente.email.trim()
        },
        notas: datosCliente.notas.trim()
      };

      await loteService.actualizarLote(loteEnEdicion._id, datosActualizacion);

      setLotes((prev) =>
        prev.map((l) =>
          l._id === loteEnEdicion._id 
            ? { 
                ...l, 
                estado: nuevoEstadoTemp, 
                cliente: datosActualizacion.cliente,
                notas: datosActualizacion.notas
              } 
            : l
        )
      );

      toast.success('Estado y cliente guardados correctamente');
      cerrarModalCliente();
    } catch (error) {
      console.error(error);
      toast.error('Error al guardar: ' + (error.message || 'Error desconocido'));
    }
  };

  // 👉 cerrar modal
  const cerrarModalCliente = () => {
    setMostrarModalCliente(false);
    setLoteEnEdicion(null);
    setNuevoEstadoTemp('');
    setDatosCliente({
      nombre: '',
      telefono: '',
      email: '',
      notas: ''
    });
    setErroresForm({});
    setEditingEstadoId(null);
  };

  // 👉 render del estado editable
  const renderEstadoEditable = (lote) => {
    if (editingEstadoId === lote._id) {
      return (
        <select
          className={`estado-select ${estadoTemp}`}
          value={estadoTemp}
          onChange={(e) => {
            const nuevoEstado = e.target.value;
            setEstadoTemp(nuevoEstado);
            actualizarEstado(lote._id, nuevoEstado);
          }}
          onBlur={() => setEditingEstadoId(null)}
          autoFocus
        >
          <option value="disponible">Disponible</option>
          <option value="reservado">Reservado</option>
          <option value="vendido">Vendido</option>
        </select>
      );
    }

    return (
      <span
        onClick={() => {
          setEditingEstadoId(lote._id);
          setEstadoTemp(lote.estado);
        }}
        style={{ cursor: 'pointer' }}
        title="Click para cambiar estado"
      >
        {getEstadoBadge(lote.estado)}
      </span>
    );
  };

  if (loading) {
    return <div className="lista-loading">Cargando lotes...</div>;
  }

  const currentUser = authService.getUser();

  return (
    <div className="lista-lotes">
      <div className="lista-header">
        <h2>Lista de Lotes</h2>
          {/* <div className="lista-user-note">Mostrando lotes de: <strong>{currentUser.name}</strong></div> */}
        <button onClick={limpiarFiltros} className="btn-limpiar">
          Limpiar Filtros
        </button>
      </div>

      <div className="filtros">
        <div className="filtro-group">
          <label>Estado:</label>
          <select name="estado" value={filterInputs.estado} onChange={handleFilterChange}>
            <option value="">Todos</option>
            <option value="disponible">Disponible</option>
            <option value="reservado">Reservado</option>
            <option value="vendido">Vendido</option>
          </select>
        </div>

        <div className="filtro-group">
          <label>Área Mín (m²):</label>
          <input
            type="number"
            name="areaMin"
            value={filterInputs.areaMin}
            onChange={handleFilterChange}
            placeholder="0"
          />
        </div>

        <div className="filtro-group">
          <label>Área Máx (m²):</label>
          <input
            type="number"
            name="areaMax"
            value={filterInputs.areaMax}
            onChange={handleFilterChange}
            placeholder="1000"
          />
        </div>

        <div className="filtro-group">
          <label>Precio Mín ($):</label>
          <input
            type="number"
            name="precioMin"
            value={filterInputs.precioMin}
            onChange={handleFilterChange}
            placeholder="0"
          />
        </div>

        <div className="filtro-group">
          <label>Precio Máx ($):</label>
          <input
            type="number"
            name="precioMax"
            value={filterInputs.precioMax}
            onChange={handleFilterChange}
            placeholder="100000"
          />
        </div>
        <div className="filtro-group filtro-actions">
          <button type="button" className="btn-aplicar" onClick={aplicarFiltros}>
            Aplicar filtros
          </button>
        </div>
      </div>

      <div className="tabla-container">
        <table className="tabla-lotes">
          <thead>
            <tr>
              <th>Número</th>
              <th>Área (m²)</th>
              <th>Precio/m²</th>
              <th>Precio Total</th>
              <th>Estado</th>
              <th>Cliente</th>
              <th>Acciones</th>
            </tr>
          </thead>
          <tbody>
            {lotes.length === 0 ? (
              <tr>
                <td colSpan="7" className="no-data">
                  No se encontraron lotes
                </td>
              </tr>
            ) : (
              lotes.map((lote) => (
                <tr key={lote._id}>
                  <td>{lote.numero}</td>
                  <td>{lote.area.toFixed(2)}</td>
                  <td>${lote.precioPorMetro.toLocaleString('es-ES')}</td>
                  <td>
                    $
                    {lote.precioTotal.toLocaleString('es-ES', {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}
                  </td>
                  {/* <td>{getEstadoBadge(lote.estado)}</td> */}
                  <td>{renderEstadoEditable(lote)}</td>
                  <td>{lote.cliente?.nombre || '-'}</td>
                  <td>
                    {currentUser && (!lote.owner || lote.owner === currentUser._id || lote.owner === currentUser.id) ? (
                      <>
                        <button onClick={() => onEditLote(lote)} className="btn-edit">
                          Editar
                        </button>
                        <button
                          onClick={() => (typeof onRequestDelete === 'function' ? onRequestDelete(lote) : onDeleteLote(lote._id))}
                          className="btn-delete"
                        >
                          Eliminar
                        </button>
                      </>
                    ) : (
                      <em>—</em>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Modal para agregar cliente */}
      {mostrarModalCliente && (
        <div className="modal-overlay" onClick={cerrarModalCliente}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Información del Cliente</h3>
              <button 
                className="modal-close" 
                onClick={cerrarModalCliente}
              >
                ×
              </button>
            </div>
            
            <div className="modal-body">
              <p className="modal-subtitle">
                Lote: <strong>{loteEnEdicion?.numero}</strong> - Cambio a: <strong>{nuevoEstadoTemp}</strong>
              </p>

              <div className="form-group">
                <label>Nombre Completo *</label>
                <input
                  type="text"
                  name="nombre"
                  value={datosCliente.nombre}
                  onChange={handleDatosClienteChange}
                  placeholder="Ej: Juan Pérez García"
                  className={erroresForm.nombre ? 'input-error' : ''}
                />
                {erroresForm.nombre && (
                  <span className="error-message">{erroresForm.nombre}</span>
                )}
              </div>

              <div className="form-group">
                <label>Teléfono *</label>
                <input
                  type="tel"
                  name="telefono"
                  value={datosCliente.telefono}
                  onChange={handleDatosClienteChange}
                  placeholder="Ej: +56912345678"
                  className={erroresForm.telefono ? 'input-error' : ''}
                />
                {erroresForm.telefono && (
                  <span className="error-message">{erroresForm.telefono}</span>
                )}
              </div>

              <div className="form-group">
                <label>Email</label>
                <input
                  type="email"
                  name="email"
                  value={datosCliente.email}
                  onChange={handleDatosClienteChange}
                  placeholder="cliente@ejemplo.com"
                />
              </div>

              <div className="form-group">
                <label>Notas del Lote</label>
                <textarea
                  name="notas"
                  value={datosCliente.notas}
                  onChange={handleDatosClienteChange}
                  placeholder="Notas adicionales sobre el lote..."
                  rows="3"
                />
              </div>
            </div>

            <div className="modal-footer">
              <button 
                onClick={cerrarModalCliente} 
                className="btn-cancelar"
              >
                Cancelar
              </button>
              <button 
                onClick={handleGuardarCliente} 
                className="btn-guardar"
              >
                Guardar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ListaLotes;
